import { Autocomplete as MuiAutocomplete } from "@mui/material";

import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { useEffect, useState, forwardRef, useCallback } from "react";
import { FormController } from "./Controller";
import { queryClient } from "$utils/queryClient";
import { normalizeText } from "$utils/utils";

const WrapperAutocomplete = forwardRef(
  (
    {
      queryFunction,
      queryFunctionName,
      multiple = false,
      filters = {},
      value,
      name,
      lookingFor,
      label,
      placeholder,
      onChange,
      onChangeExt,
      inputProps = {},
      options = [],
      withInit = false,
      getOptionLabel,
      getOptionSelected,
      cacheTime = 60 * 1000,
      ...rest
    }: any,
    ref: any
  ) => {
    const [open, setOpen] = useState(false);
    const [optionState, setOptions] = useState(options);
    const [loading, setIsLoading] = useState(false);

    const fetchOptions = useCallback(async () => {
      setIsLoading(true);

      const queryKeys = [
        queryFunctionName,
        {
          ...filters,
          rowsPerPage: 10000,
        },
      ];
      const cacheData = queryClient.getQueryData(queryKeys) as any;

      let data;
      if (cacheData?.data) {
        data = cacheData.data;
      } else if (!cacheData?.data) {
        const res = await queryClient.fetchQuery(
          queryKeys,
          () => {
            return queryFunction?.({
              ...filters,
              rowsPerPage: 10000, // this is an arbirtrary value, but anyway, if you got more than 10000 result, you shouldn't use the autocomplete
            });
          },
          { cacheTime }
        );
        await queryClient.setQueryData(queryKeys, res);
        data = res?.data;
      }

      if (multiple && value.length > 0) {
        setOptions([...value, ...(data ?? [])]);
      } else {
        setOptions(data?.length ? data : []);
      }
      setIsLoading(false);
    }, [cacheTime, filters, multiple, queryFunction, queryFunctionName, value]);

    useEffect(() => {
      if (withInit) {
        fetchOptions();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withInit]);

    return (
      <MuiAutocomplete
        ref={ref}
        multiple={multiple}
        value={value}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={optionState || []}
        getOptionLabel={
          getOptionLabel || ((option: any) => option?.[lookingFor] || option)
        }
        filterOptions={(options: any, value: any) => {
          const search = normalizeText(value.inputValue);
          return options.filter((option: any) => {
            const opt =
              getOptionLabel?.(option) || option?.[lookingFor] || option;
            return normalizeText(opt).includes(search);
          });
        }}
        getOptionSelected={(option: any, value: any) => {
          if (getOptionSelected) {
            return getOptionSelected(option, value);
          }
          if (lookingFor) {
            return (
              normalizeText(option?.[lookingFor]) ===
              normalizeText(value?.[lookingFor])
            );
          }
          return option === value;
        }}
        onChange={(_event, newValue) => {
          if (!multiple) {
            onChange?.(newValue);
            onChangeExt?.(newValue);
          }
        }}
        {...rest}
        renderInput={(params) => (
          <TextField
            name={name}
            {...params}
            label={label}
            placeholder={placeholder}
            variant="outlined"
            onFocus={(e) => e.target.select()}
            InputProps={{
              ...params.InputProps,
              ...inputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      data-testid={`${lookingFor}-loading`}
                    />
                  ) : null}

                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    );
  }
);

export const Autocomplete = ({
  withControl = true,
  label,
  name,
  rules,
  required = false,
  onChange,
  ...props
}: any) => {
  if (withControl) {
    if (!props.queryFunctionName) {
      throw new Error("mandatory queryFunctionName");
    }
    return (
      <FormController
        label={label}
        name={name}
        rules={rules}
        required={required}
      >
        {({ value = props.multiple ? [] : "", ...field }) => (
          <WrapperAutocomplete
            name={name}
            label={label}
            onChangeExt={onChange || undefined}
            value={value}
            {...field}
            {...props}
          />
        )}
      </FormController>
    );
  }

  return (
    <WrapperAutocomplete
      name={name}
      label={label}
      onChangeExt={onChange || undefined}
      {...props}
    />
  );
};
